import Badge from "@/js/components/Badge/Badge";
import { AccountTypes, UIState } from '@/utils/Enums';
import BaseTable from "@/js/components/BaseTable/BaseTable";
import React from "react";
import { __ } from "@/utils/Trans";
import adminStore from "@/js/modules/admin/store/AdminStore";
import { useParams, Link } from 'react-router-dom';
import { PageRoutes } from "@/utils/PageRoutes";
import TextBox from '@/js/components/TextBox/TextBox';
import { RowStates } from "@/js/core/enums/RowStates";
import MoreAction from "@/js/components/MoreAction/MoreAction";
import DeleteUserModal from "../components/DeleteUserModal";
import CopyButton from "@/js/components/CopyButton/CopyButton";
import UpdateUserModal from "../components/UpdateUserModal";
import UserDetailModal from "../components/UserDetailModal";

export const AdminUserListFilter = { ...AccountTypes, ...RowStates, All: 'all', } as const

const AdminUserListPage = () => {
	adminStore.initializeStore();

	const params = useParams<{ accountType: string, state: string }>();

	const [search, setSearch] = React.useState('')
	const [selectedAccountType, setSelectedAccountType] = React.useState(params.accountType)
	const [showDeleteModal, setShowDeleteModal] = React.useState(false)
	const [showUpdateModal, setShowUpdateModal] = React.useState(false)
	const [showDetailModal, setShowDetailModal] = React.useState(false)
	const [user, setUser] = React.useState()

	const columns = [
		{ name: __('AdminUserListPage.Table.Id'), classes: 'text-white' },
		{ name: __('AdminUserListPage.Table.Name'), classes: 'text-white' },
		{ name: __('AdminUserListPage.Table.Email'), classes: 'text-white' },
		{ name: __('AdminUserListPage.Table.CreatedAndLastLoginDate'), classes: 'text-white' },
		{ name: __('AdminUserListPage.Table.CommunicationPermission'), classes: 'text-white' },
		{ name: __('AdminUserListPage.Table.UserType'), classes: 'text-white' },
		{ name: __('AdminUserListPage.Table.BlokName'), classes: 'text-white' },
		{ name: __('AdminUserListPage.Table.CompanyName'), classes: 'text-white' },
		{ name: __('Common.Table.Actions'), classes: 'text-white' },
		{ name: __('AdminUserListPage.Table.Status'), classes: 'text-white' },
	];

	const handleGetUserStatus = (user) => {
		if (user.accountType === AccountTypes.Talent) {
			return <span className={"text-white"}>-</span>;
		}

		if (user.accountType === AccountTypes.CompanyOwner) {
			return user.companyState === RowStates.Approved
				? (
					<Badge type={UIState.Success}>
						{__('AdminUserListPage.Table.Status.Approved')}
					</Badge>
				) : (
					<Badge type={UIState.Warning}>
						{__('AdminUserListPage.Table.Status.Restricted')}
					</Badge>
				)
		}

		if (user.accountType === AccountTypes.BlokOwner) {
			switch (user.blokState) {
				case RowStates.Approved:
					return (
						<Badge type={UIState.Success}>
							{__('AdminUserListPage.Table.Status.Approved')}
						</Badge>
					)
				case RowStates.Declined:
					return (
						<Badge type={UIState.Danger}>
							{__("AdminUserListPage.Table.Status.Rejected")}
						</Badge>
					)
				case RowStates.Pending:
					return (
						<Badge type={UIState.Warning}>
							{__('AdminUserListPage.Table.Status.Pending')}
						</Badge>
					)
				default:
					return (
						<Badge type={UIState.Default}>
							-
						</Badge>
					)
			}
		}
	}

	React.useEffect(() => {
		setSelectedAccountType(params.accountType);

		adminStore.fetchUsers(params.accountType!, params.state!);
	}, [params]);


	const handleDeleteUser = (user) => {
		setShowDeleteModal(true)
		setUser(user)
	}

	const handleUpdateUser = (user) => {
		setShowUpdateModal(true)
		setUser(user)
	}

	const handleDetailUser = (user) => {
		setShowDetailModal(true)
		setUser(user)
	}

	return (
		<div className={"ml-8 mt-5 bg-gray-900"}>
			<h1 className={"text-3xl font-semibold text-white"}>{__('AdminUserListPage.Title')}</h1>
			{adminStore.state.isLoading ? null : <>
				<div className={"mt-8"}>
					<TextBox name={"title"} classes={"w-96"}>
						<TextBox.Input
							name={"search"}
							classes={""}
							attr={{
								onChange: e => setSearch(e.target.value),
								name: "search",
								placeholder: __('AdminUserListPage.SearchText')
							}}
						/>
					</TextBox>
				</div>

				<div className="!text-white">
					<BaseTable columns={columns} classes={"max-w-screen-xl "}>
						{adminStore.filterByAccountTypeAndSearch(adminStore.state.users, selectedAccountType === AdminUserListFilter.All ? '' : selectedAccountType, search).map((user, index) =>
							<BaseTable.Row key={index}>
								<BaseTable.Row.Cell>
									<p className={"text-white flex items-center"}>{user.id.slice(0, 8)}...<CopyButton value={user.id} /></p>

								</BaseTable.Row.Cell>
								<BaseTable.Row.Cell>
									<Link target={"_blank"} to={PageRoutes.TalentProfilePage.replace(":id", user.id)}>
										<p className={"text-white hover:text-gray-100"}>{user.firstName} - {user.lastName}</p>
									</Link>
								</BaseTable.Row.Cell>
								<BaseTable.Row.Cell>
									<p className={"text-white"}>{user.email}</p>
								</BaseTable.Row.Cell>
								<BaseTable.Row.Cell>
									<p className={"text-white flex flex-col"}>
										<span>{adminStore.getDateByFormat(user.created)}</span>
										<span>- {adminStore.getDateByFormat(user.lastLoginDate)}</span>
									</p>
								</BaseTable.Row.Cell>
								<BaseTable.Row.Cell>
									<p className={"text-white"}>
										{user.hasNotificationPermission ? __('AdminUserListPage.Yes') : __('AdminUserListPage.No')}
									</p>
								</BaseTable.Row.Cell>
								<BaseTable.Row.Cell>
									<p className={"text-white"}>
										<Badge type={adminStore.getAccountType(user.accountType).type}>
											{adminStore.getAccountType(user.accountType).text}
										</Badge>
									</p>
								</BaseTable.Row.Cell>
								<BaseTable.Row.Cell>
									<p className={"text-white"}>
										{user.blokName
											? user.blokName.length > 20
												? user.blokName.slice(0, 20) + '...'
												: user.blokName
											: ' - '}
									</p>
								</BaseTable.Row.Cell>
								<BaseTable.Row.Cell>
									<p className={"text-white"}>
										{user.companyName
											? user.companyName.length > 20
												? user.companyName.slice(0, 20) + '...'
												: user.companyName
											: ' - '}
									</p>
								</BaseTable.Row.Cell>
								<BaseTable.Row.Cell>
									<MoreAction name={"MoreAction"}>
										<button onClick={() => handleDeleteUser(user)}>
											{"Sil"}
										</button>
										<button onClick={() => handleUpdateUser(user)}>
											{"Düzenle"}
										</button>
										<button onClick={() => handleDetailUser(user)}>
											{"Detay"}
										</button>
									</MoreAction>
								</BaseTable.Row.Cell>
								<BaseTable.Row.Cell>
									{handleGetUserStatus(user)}
								</BaseTable.Row.Cell>
							</BaseTable.Row>
						)}
					</BaseTable>
				</div>
			</>}

			{showUpdateModal && <UpdateUserModal open={showUpdateModal}
				onClose={function (): void {
					setShowUpdateModal(false)
				}}
				user={user}
				onSuccess={function (): void {
					adminStore.fetchUsers(params.accountType!, params.state!);
				}}
			/>}

			{showDeleteModal && <DeleteUserModal open={showDeleteModal}
				onClose={function (): void {
					setShowDeleteModal(false)
				}}
				user={user}
				onSuccess={function (): void {
					adminStore.fetchUsers(params.accountType!, params.state!);
				}}
			/>}

			{showDetailModal && <UserDetailModal open={showDetailModal}
				onClose={function (): void {
					setShowDetailModal(false)
				}}
				user={user}
				onSuccess={function (): void {
					adminStore.fetchUsers(params.accountType!, params.state!);
				}}
			/>}
		</div >
	)
}

export default AdminUserListPage;
